import * as rxjs from "rxjs";
import { createDispatcher, type Dispatcher } from "@utils/dispatch";
import { type ChiContext, currentRuntime } from "@/runtime.tsx";

export type Deps = readonly unknown[];

const sameDeps = (a: Deps | undefined, b: Deps | undefined) =>
  a !== undefined && b !== undefined && a.length === b.length &&
  a.every((value, i) => Object.is(value, b[i]));

export function useContext<T>(context: ChiContext<T>): T {
  for (let scope = currentRuntime().scope; scope; scope = scope.parent) {
    if (scope.context === context) return scope.value as T;
  }
  return context.defaultValue;
}

export function useRef<T>(initial: T): { current: T } {
  return currentRuntime().slot(() => ({ current: initial }));
}

export function useMemo<T>(compute: () => T, deps: Deps): T {
  const slot = currentRuntime().slot(() => ({
    deps: undefined as Deps | undefined,
    value: undefined as T,
  }));
  if (!sameDeps(slot.deps, deps)) {
    slot.value = compute();
    slot.deps = deps;
  }
  return slot.value;
}

export function useEffect(effect: () => void | (() => void), deps?: Deps): void {
  const runtime = currentRuntime();
  const slot = runtime.slot(() => ({
    deps: undefined as Deps | undefined,
    cleanup: undefined as void | (() => void),
    dispose() {
      this.cleanup?.();
      this.cleanup = undefined;
    },
  }));

  if (deps !== undefined && sameDeps(slot.deps, deps)) return;
  slot.deps = deps;
  runtime.afterPass(() => {
    slot.cleanup?.();
    slot.cleanup = effect();
  });
}

interface ReducerSlot<S, A> {
  reducer: (state: S, action: A) => S;
  dispatcher: Dispatcher<{ reduce: A }, S>;
  dispatch: (action: A) => void;
  dispose: () => void;
}

export function useReducer<S, A>(
  reducer: (state: S, action: A) => S,
  initial: S | (() => S),
): [S, (action: A) => void] {
  const runtime = currentRuntime();
  const slot = runtime.slot<ReducerSlot<S, A>>(() => {
    const self = { reducer } as ReducerSlot<S, A>;
    const dispatcher = createDispatcher<{ reduce: A }, S>({
      initialState: typeof initial === "function" ? (initial as () => S)() : initial,
      handlers: { reduce: ({ state, payload }) => self.reducer(state, payload as A) },
    });
    const sub = dispatcher.state$.pipe(rxjs.skip(1)).subscribe(() => runtime.invalidate());

    self.dispatcher = dispatcher;
    self.dispatch = (action) => {
      dispatcher.dispatch({ type: "reduce", payload: action } as never);
    };
    self.dispose = () => {
      sub.unsubscribe();
      dispatcher.dispose();
    };
    return self;
  });

  slot.reducer = reducer;
  return [slot.dispatcher.getState(), slot.dispatch];
}

const setReducer = <T,>(state: T, next: T | ((state: T) => T)): T =>
  typeof next === "function" ? (next as (state: T) => T)(state) : next;

export function useState<T>(initial: T | (() => T)): [T, (next: T | ((state: T) => T)) => void] {
  return useReducer<T, T | ((state: T) => T)>(setReducer, initial);
}

export function useObservable<T>(source: rxjs.Observable<T>): T | undefined;
export function useObservable<T>(source: rxjs.Observable<T>, initial: T): T;
export function useObservable<T>(source: rxjs.Observable<T>, initial?: T): T | undefined {
  const runtime = currentRuntime();
  const slot = runtime.slot(() => ({
    source: null as rxjs.Observable<T> | null,
    sub: null as rxjs.Subscription | null,
    value: initial,
    dispose() {
      this.sub?.unsubscribe();
      this.sub = null;
    },
  }));

  if (slot.source !== source) {
    slot.sub?.unsubscribe();
    slot.source = source;
    let syncing = true;
    slot.sub = source.subscribe((value) => {
      slot.value = value;
      if (!syncing) runtime.invalidate();
    });
    syncing = false;
  }
  return slot.value;
}

export function useSelect<A extends Record<string, any>, S, P, T>(
  dispatcher: Dispatcher<A, S, P>,
  selector: (state: S) => T,
  isEqual: (a: T, b: T) => boolean = Object.is,
): T {
  const runtime = currentRuntime();
  const slot = runtime.slot(() => ({
    dispatcher: null as Dispatcher<A, S, P> | null,
    sub: null as rxjs.Subscription | null,
    selector,
    isEqual,
    last: undefined as T,
    dispose() {
      this.sub?.unsubscribe();
      this.sub = null;
    },
  }));

  slot.selector = selector;
  slot.isEqual = isEqual;
  const value = selector(dispatcher.getState());

  if (slot.dispatcher !== dispatcher) {
    slot.sub?.unsubscribe();
    slot.dispatcher = dispatcher;
    slot.last = value;
    slot.sub = dispatcher.state$.subscribe((state) => {
      const next = slot.selector(state);
      if (slot.isEqual(slot.last, next)) return;
      slot.last = next;
      runtime.invalidate();
    });
  }
  slot.last = value;
  return value;
}
